"use strict";
// =========================================================================
// Pure FormDefinition → HTML renderer.
//
// Given a published FormDefinition + a submission's answers + computedScores,
// produces a self-contained HTML document ready to hand to ANY html→pdf
// engine (WeasyPrint service, headless Chromium, etc.). No IO, no framework,
// no engine coupling — safe to reuse from the FE for an in-browser print
// preview as well as from the NestJS backend's PDF pipeline.
//
// The FormDefinition is self-describing: option labels, section titles,
// scoring, and visibleIf rules all live in the definition, so this renderer
// works for the DD assessment today and every future React form unchanged —
// no per-form templates.
// =========================================================================
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderFormSubmissionHtml = void 0;
const form_definition_1 = require("../form-validation/form-definition");
const form_pdf_styles_1 = require("./form-pdf-styles");
const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const esc = (v) => String(v !== null && v !== void 0 ? v : '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
/** Format an ISO/date string as "31 Dec 2026". Falls back to the raw string. */
const formatDate = (raw) => {
    var _a;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (!m)
        return raw;
    const [, y, mo, d] = m;
    const month = (_a = MONTHS[Number(mo) - 1]) !== null && _a !== void 0 ? _a : mo;
    return `${Number(d)} ${month} ${y}`;
};
/** Format a date-time ISO string as "31 Dec 2026, 14:23". */
const formatDateTime = (raw) => {
    const date = formatDate(raw);
    const t = /T(\d{2}):(\d{2})/.exec(raw);
    return t ? `${date}, ${t[1]}:${t[2]}` : date;
};
const formatBytes = (n) => {
    if (n == null || !Number.isFinite(n))
        return '';
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const optionByValue = (q, value) => { var _a; return (_a = q.options) === null || _a === void 0 ? void 0 : _a.find((o) => o.value === value); };
const isEmpty = (v) => v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
const renderAlert = (a) => {
    var _a;
    const bullets = ((_a = a.bullets) === null || _a === void 0 ? void 0 : _a.length)
        ? `<ul>${a.bullets.map((b) => `<li>${esc(b)}</li>`).join('')}</ul>`
        : '';
    return `<div class="alert ${a.severity}"><p class="alert-body">${esc(a.body)}</p>${bullets}</div>`;
};
const renderAlerts = (alerts) => (alerts !== null && alerts !== void 0 ? alerts : []).map(renderAlert).join('');
/** Render just the answer value of a single question as HTML. */
const renderAnswerValue = (q, value, files) => {
    var _a;
    switch (q.type) {
        case 'YES_NO':
            return value === 'YES' ? 'Yes' : value === 'NO' ? 'No' : esc(value);
        case 'CONFIRMATION':
            return value === true
                ? '✓ Confirmed'
                : '<span class="empty">Not confirmed</span>';
        case 'DATE':
            return typeof value === 'string' ? esc(formatDate(value)) : esc(value);
        case 'DROPDOWN':
        case 'RATING': {
            const opt = optionByValue(q, value);
            const label = opt ? opt.label : String(value);
            const prefix = typeof value === 'number' ? `${value} — ` : '';
            const dq = (opt === null || opt === void 0 ? void 0 : opt.disqualifies) ? '<span class="tag">disqualifying</span>' : '';
            return `${esc(prefix)}${esc(label)}${dq}`;
        }
        case 'MULTI_CHECKBOX': {
            const arr = Array.isArray(value) ? value : [];
            const items = arr
                .map((v) => { var _a, _b; return esc((_b = (_a = optionByValue(q, v)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : v); })
                .map((l) => `<li>${l}</li>`)
                .join('');
            return `<ul class="multi">${items}</ul>`;
        }
        case 'FILE': {
            // FILE answers are FileAnswer[] objects keyed by storageObjectVersionId;
            // display metadata (name/size/url) comes from the caller-supplied
            // `files` map, falling back to the answer's own optional fields.
            const list = (Array.isArray(value) ? value : []).filter((v) => !!v &&
                typeof v === 'object' &&
                typeof v.storageObjectVersionId === 'string');
            const items = list
                .map((fa) => {
                var _a, _b, _c, _d;
                const meta = (_a = files[fa.storageObjectVersionId]) !== null && _a !== void 0 ? _a : {};
                const name = esc((_c = (_b = meta.filename) !== null && _b !== void 0 ? _b : fa.filename) !== null && _c !== void 0 ? _c : fa.storageObjectVersionId);
                const size = formatBytes((_d = meta.sizeBytes) !== null && _d !== void 0 ? _d : fa.sizeBytes);
                const metaHtml = size ? ` <span class="fmeta">(${size})</span>` : '';
                // Clickable link only when the caller resolved a URL.
                const label = meta.url ? `<a href="${esc(meta.url)}">${name}</a>` : name;
                return `<li>${label}${metaHtml}</li>`;
            })
                .join('');
            return `<ul class="files">${items}</ul>`;
        }
        case 'REPEATER': {
            const cols = (_a = q.columns) !== null && _a !== void 0 ? _a : [];
            const rows = (Array.isArray(value) ? value : []);
            const head = cols.map((c) => `<th>${esc(c.label)}</th>`).join('');
            const body = rows
                .map((row) => `<tr>${cols
                .map((c) => `<td>${esc(row[c.key])}</td>`)
                .join('')}</tr>`)
                .join('');
            return `<table class="repeater"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
        }
        // TEXT / TEXTAREA / RICH_TEXT and anything else: plain text.
        // NB: RICH_TEXT is escaped (shown verbatim) until an HTML sanitiser is
        // wired in — do not inject raw stored markup into the PDF.
        default:
            return esc(value);
    }
};
/** Render one question as a label + answer block. INFO blocks handled separately. */
const renderQuestion = (q, answers, showEmpty, files) => {
    const value = answers[q.id];
    const empty = isEmpty(value);
    if (empty && !showEmpty)
        return '';
    const help = q.helpText ? `<div class="help">${esc(q.helpText)}</div>` : '';
    const req = q.required ? '<span class="req">*</span>' : '';
    const alerts = renderAlerts(q.alerts);
    const answerHtml = empty
        ? '<div class="answer empty">—</div>'
        : `<div class="answer">${renderAnswerValue(q, value, files)}</div>`;
    return `
      <div class="qa">
        <div class="label">${esc(q.label)}${req}</div>
        ${help}
        ${alerts}
        ${answerHtml}
      </div>`;
};
const renderInfoBlock = (q) => {
    const title = q.label ? `<div class="info-title">${esc(q.label)}</div>` : '';
    const body = q.body ? `<div class="info-body">${esc(q.body)}</div>` : '';
    const alerts = renderAlerts(q.alerts);
    if (!title && !body && !alerts)
        return '';
    return `<div class="info-block">${title}${body}${alerts}</div>`;
};
const sectionHeading = (s) => s.headingNumber != null ? `${s.headingNumber}. ${s.title}` : s.title;
const renderSection = (s, answers, showEmpty, files) => {
    var _a;
    const parts = [];
    let currentSubsection;
    for (const q of s.questions) {
        if (q.softDeleted)
            continue;
        const visible = q.visibleIf ? (0, form_definition_1.evaluateCondition)(q.visibleIf, answers) : true;
        if (!visible)
            continue;
        // Emit a sub-heading whenever the subsection label changes.
        if (q.subsection && q.subsection !== currentSubsection) {
            currentSubsection = q.subsection;
            const desc = q.subsectionDescription
                ? `<div class="subsection-desc">${esc(q.subsectionDescription)}</div>`
                : '';
            parts.push(`<div class="subsection-title">${esc(q.subsection)}</div>${desc}`);
        }
        parts.push(q.type === 'INFO' ? renderInfoBlock(q) : renderQuestion(q, answers, showEmpty, files));
    }
    const body = parts.join('');
    if (!body.trim() && !((_a = s.alerts) === null || _a === void 0 ? void 0 : _a.length))
        return '';
    const subHeader = s.subHeader ? `<div class="section-subheader">${esc(s.subHeader)}</div>` : '';
    return `
      <section class="form-section">
        <h2>${esc(sectionHeading(s))}</h2>
        ${subHeader}
        ${renderAlerts(s.alerts)}
        ${body}
      </section>`;
};
const renderScores = (def, scores) => {
    const scored = def.sections.filter((s) => s.scored && s.scoreKey);
    if (scored.length === 0)
        return '';
    const rows = scored
        .map((s) => {
        var _a;
        return `<tr><td>${esc(sectionHeading(s))}</td><td class="v">${esc((_a = scores[s.scoreKey]) !== null && _a !== void 0 ? _a : 0)}</td></tr>`;
    })
        .join('');
    const totalKey = def.scoring.totalKey;
    const totalRow = scores[totalKey] != null
        ? `<tr class="total"><td>Total</td><td class="v">${esc(scores[totalKey])}</td></tr>`
        : '';
    return `
      <div class="scores">
        <h2>Assessment Score</h2>
        <table class="scores-tbl"><tbody>${rows}${totalRow}</tbody></table>
      </div>`;
};
const renderMeta = (opts) => {
    const row = (k, v) => v == null || v === '' ? '' : `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`;
    return `<table class="meta"><tbody>
        ${row('Submission', opts.submissionVersion != null ? `v${opts.submissionVersion}` : null)}
        ${row('Submitted by', opts.submittedByName)}
        ${row('Submitted at', opts.submittedAt ? formatDateTime(opts.submittedAt) : null)}
      </tbody></table>`;
};
/**
 * Render a completed form submission to a self-contained HTML document.
 *
 * @param def      The published FormDefinition the submission was made against.
 * @param answers  The submission's `answers` map ({ [questionId]: value }).
 * @param scores   The submission's `computedScores` map.
 * @param options  Header metadata + render flags. The renderer is pure, so
 *                 timestamps (e.g. generatedAt) must be supplied by the caller.
 */
const renderFormSubmissionHtml = (def, answers, scores, options = {}) => {
    var _a, _b, _c;
    const showEmpty = (_a = options.showEmpty) !== null && _a !== void 0 ? _a : true;
    const brand = (_b = options.brand) !== null && _b !== void 0 ? _b : form_pdf_styles_1.DEFAULT_PDF_THEME;
    const files = (_c = options.attachments) !== null && _c !== void 0 ? _c : {};
    const sectionsHtml = def.sections
        .map((s) => renderSection(s, answers, showEmpty, files))
        .join('');
    const description = def.description
        ? `<div class="section-subheader">${esc(def.description)}</div>`
        : '';
    const logo = brand.logoDataUri
        ? `<img class="brand-logo" src="${esc(brand.logoDataUri)}" alt="${esc(brand.orgName)}" />`
        : '';
    const footer = options.generatedAt
        ? `<div class="doc-footer">Generated ${esc(formatDateTime(options.generatedAt))} · ${esc(brand.orgName)}</div>`
        : '';
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${esc(def.title)}</title>
  <style>${(0, form_pdf_styles_1.buildFormPdfStyles)(brand)}</style>
</head>
<body>
  <div class="doc-header">
    <div class="brand-bar"></div>
    ${logo}
    <h1>${esc(def.title)}</h1>
    <div class="subtitle">${esc(brand.orgName)}</div>
    ${description}
    ${renderMeta(options)}
  </div>
  ${renderScores(def, scores)}
  ${sectionsHtml}
  ${footer}
</body>
</html>`;
};
exports.renderFormSubmissionHtml = renderFormSubmissionHtml;
